import React, { useState, useRef, Component } from 'react';
import { motion, useInView, AnimatePresence } from 'framer-motion';
import { PlusIcon, MinusIcon } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { JoinNetworkForm } from './JoinNetworkForm';
interface FAQItemData {
  questionKey: string;
  answerKey: string;
}
const faqItems: FAQItemData[] = [{
  questionKey: 'faq.q1',
  answerKey: 'faq.a1'
}, {
  questionKey: 'faq.q2',
  answerKey: 'faq.a2'
}, {
  questionKey: 'faq.q3',
  answerKey: 'faq.a3'
}, {
  questionKey: 'faq.q4',
  answerKey: 'faq.a4'
}, {
  questionKey: 'faq.q5',
  answerKey: 'faq.a5'
}, {
  questionKey: 'faq.q6',
  answerKey: 'faq.a6'
}];
function FAQItemComponent({
  item,
  index
}: {
  item: FAQItemData;
  index: number;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const inView = useInView(ref, {
    once: true,
    margin: '-40px'
  });
  const {
    t
  } = useLanguage();
  return <motion.div ref={ref} initial={{
    opacity: 0,
    y: 24
  }} animate={inView ? {
    opacity: 1,
    y: 0
  } : {}} transition={{
    duration: 0.5,
    delay: index * 0.07,
    ease: [0.16, 1, 0.3, 1]
  }} className="border-b border-gray-200 last:border-none" data-id="element-1577">
      <button onClick={() => setOpen(!open)} aria-expanded={open} className="w-full flex items-center justify-between gap-4 py-6 text-left group" data-id="element-1578">
        <span className="font-nunito font-bold text-base sm:text-lg text-haa-navy group-hover:text-haa-blue transition-colors duration-200 leading-snug" data-id="element-1579">
          {t(item.questionKey)}
        </span>
        <motion.div className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center transition-colors duration-200 ${open ? 'bg-haa-blue text-white' : 'bg-gray-100 text-haa-navy group-hover:bg-haa-blue/10 group-hover:text-haa-blue'}`} data-id="element-1580">
          {open ? <MinusIcon className="w-4 h-4" data-id="element-1581" /> : <PlusIcon className="w-4 h-4" data-id="element-1582" />}
        </motion.div>
      </button>

      <AnimatePresence initial={false} data-id="element-1583">
        {open && <motion.div key="answer" initial={{
        height: 0,
        opacity: 0
      }} animate={{
        height: 'auto',
        opacity: 1
      }} exit={{
        height: 0,
        opacity: 0
      }} transition={{
        duration: 0.35,
        ease: [0.16, 1, 0.3, 1]
      }} className="overflow-hidden" data-id="element-1584">
            <p className="font-nunito text-gray-600 text-base leading-relaxed pb-6 pr-12" data-id="element-1585">
              {t(item.answerKey)}
            </p>
          </motion.div>}
      </AnimatePresence>
    </motion.div>;
}
export function FAQ() {
  const titleRef = useRef<HTMLDivElement>(null);
  const titleInView = useInView(titleRef, {
    once: true
  });
  const [formOpen, setFormOpen] = useState(false);
  const {
    t
  } = useLanguage();
  return <section id="faq" className="bg-gray-50 py-16 lg:py-24" aria-labelledby="faq-heading" data-id="element-1586">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1587">
        <div ref={titleRef} className="text-center mb-14" data-id="element-1588">
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} animate={titleInView ? {
          opacity: 1,
          y: 0
        } : {}} transition={{
          duration: 0.6
        }} data-id="element-1589">
            <span className="font-nunito text-haa-blue text-sm uppercase tracking-[0.3em] font-semibold" data-id="element-1590">
              {t('faq.label')}
            </span>
            <h2 id="faq-heading" className="font-bebas text-6xl sm:text-7xl lg:text-8xl text-haa-navy tracking-wider mt-2" data-id="element-1591">
              {t('faq.heading')}
            </h2>
            <div className="flex items-center justify-center gap-3 mt-4" data-id="element-1592">
              <div className="w-12 h-1 bg-haa-blue rounded-full" data-id="element-1593" />
              <div className="w-3 h-3 rounded-full bg-haa-skyblue" data-id="element-1594" />
              <div className="w-12 h-1 bg-haa-midblue rounded-full" data-id="element-1595" />
            </div>
            <p className="font-nunito text-gray-600 text-lg mt-6 max-w-2xl mx-auto" data-id="element-1596">
              {t('faq.description')}
            </p>
          </motion.div>
        </div>

        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={titleInView ? {
        opacity: 1,
        y: 0
      } : {}} transition={{
        delay: 0.2,
        duration: 0.5
      }} className="bg-white rounded-3xl shadow-lg px-6 sm:px-10 divide-y divide-gray-200" data-id="element-1597">
          {faqItems.map((item, i) => <FAQItemComponent key={i} item={item} index={i} data-id="element-1598" />)}
        </motion.div>

        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={titleInView ? {
        opacity: 1,
        y: 0
      } : {}} transition={{
        delay: 0.5,
        duration: 0.5
      }} className="mt-12 text-center" data-id="element-1599">
          <p className="font-nunito text-gray-500 text-base mb-4" data-id="element-1600">
            {t('faq.contact.text')}
          </p>
          <motion.button onClick={() => setFormOpen(true)} whileHover={{
          scale: 1.05
        }} whileTap={{
          scale: 0.97
        }} className="inline-flex items-center gap-2 bg-gradient-to-r from-haa-blue to-haa-midblue text-white font-nunito font-bold text-base px-8 py-4 rounded-full shadow-lg hover:shadow-xl transition-shadow" data-id="element-1601">
            {t('faq.contact.button')}
          </motion.button>
        </motion.div>
      </div>

      <JoinNetworkForm isOpen={formOpen} onClose={() => setFormOpen(false)} data-id="element-1602" />
    </section>;
}